import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { OrganizationRole } from "../../common/models/organization-role";
import { User } from "../../common/models/user";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { OrganizationMembership } from "../../common/models/organization-membership";
import { Coach } from "../../common/models/coach";
import { Athlete } from "../../common/models/athlete";
import { FeedbackItemRightsList } from "../../feedback/providers/feedback-rights";

export class TestRights {
    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    private _itemRightsList: TestItemRightsList | null = null;
    private _screenRightsList: TestScreenRightList | null = null;
    private _paramsItem: TestItemRightsParams | null = null;
    private _paramsScreen: TestScreenRightsParams | null = null;
    private _bestOfItemRight = 10000;
    private _bestOfScreenRight = 10000;
    constructor(private _utils: UtilsProvider) {
        this.setDefault();
    }


    public evaluateItemRight(params: TestItemRightsParams): void {

        /**
         * Les paramètres reçus contiennent le test/measure, le user.id
         * et le organizationId. Les droits évalués doivent répondrent
         * à la question : qu'est-ce que je (user.id) peux faire avec
         * ce test/measure (test.id) dans cette organisation (organizationId)
         * avec mon rôle (this._bestOfItemRight).
         */

        this._paramsItem = params;
        this.setDefault();
        this.setBestOfRole();

        const athlete = this._paramsItem.athlete;

        if (!this._itemRightsList) return;
        switch (this._bestOfItemRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            // tous les tests
            this._itemRightsList.view = true;
            this._itemRightsList.edit = true;
            this._itemRightsList.delete = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un supercoach
            // tous les tests de son organisation
            const memberships = this._utils.findManyIn([athlete.userId], this._paramsItem.athleteMemberships, 'userId');
            const orgIds = this._utils.getKeyValues(memberships, 'organizationId');

            if (orgIds.indexOf(this._paramsItem.organizationId) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.edit = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            // tous les tests ayant une relation avec lui
            const links = this._utils.findManyIn([athlete.id], this._paramsItem.athleteCoachRelations, 'athleteId');
            const coachIds = this._utils.getKeyValues(links, 'coachId');
            const me = this._utils.findIn(this._paramsItem.user.id, this._paramsItem.coaches, 'userId');

            if (coachIds.indexOf(me.id) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.edit = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            // uniquement ses tests
            this._itemRightsList.view = (this._paramsItem.user.id === athlete.userId);
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            // idem que le coach mais en lecture seule
            // TODO pour le moment, il n'y a pas de table qui lie un guest à un athlète, donc aucun droit
            // this._itemRightsList.view = true;
            break;

        default:
            // rien
        }
    }

    public evaluateScreenRights(params: TestScreenRightsParams): void {

        /**
         * Détermine les droits que l'utilisateur possède
         * pour les commandes générales des écrans.
         */

        this._paramsScreen = params;
        this.setDefault();
        this.setBestOfRole();

        if (!this._screenRightsList) return;

        switch (this._bestOfScreenRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un super-coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            this._screenRightsList.list = true;
            this._screenRightsList.add = false;
            this._screenRightsList.edit = false;
            this._screenRightsList.delete = false;
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            this._screenRightsList.list = true;
            this._screenRightsList.add = false;
            this._screenRightsList.edit = false;
            this._screenRightsList.delete = false;
            break;

        default:
            // rien
        }
    }

    public getItemRightsList(): TestItemRightsList {
        if (!this._itemRightsList) throw new Error('ItemRightsList is null');
        return this._itemRightsList;
    }

    public getScreenRightsList(): TestScreenRightList {
        if (!this._screenRightsList) throw new Error('ScreenRightsList is null');
        return this._screenRightsList;
    }

    public getItemParams(): TestItemRightsParams {
        if(!this._paramsItem) throw new Error('ItemParams is null');
        return this._paramsItem;
    }

    public getScreenParams(): TestScreenRightsParams {
        if(!this._paramsScreen) throw new Error('ScreenParams is null');
        return this._paramsScreen;
    }

    public get LIST() {
        if (this._screenRightsList) {
            return this._screenRightsList.list;
        }
        return false;
    }

    public get ADD() {
        if (this._screenRightsList) {
            return this._screenRightsList.add;
        }
        return false;
    }

    public get EDIT() {
        if (this._screenRightsList) {
            return this._screenRightsList.edit;
        }
        return false;
    }

    public get DELETE() {
        if (this._screenRightsList) {
            return this._screenRightsList.delete;
        }
        return false;
    }

    private setDefault(): void {
      this._itemRightsList = {
          view: false,
          edit: false,
          duplicate: false,
          delete: false,
        };

      this._screenRightsList = {
          list: false,
          add: false,
          edit: false,
          delete: false,
        };

        this._bestOfItemRight = 10000;
        this._bestOfScreenRight = 10000;
    }

    private setBestOfRole() {

        if (this._paramsItem && this._paramsItem.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsItem.rolesIds.length; i++) {
                if (this._paramsItem.rolesIds[i] < this._bestOfItemRight) {
                    this._bestOfItemRight = this._paramsItem.rolesIds[i];
                }
            }
        }

        if (this._paramsScreen && this._paramsScreen.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsScreen.rolesIds.length; i++) {
                if (this._paramsScreen.rolesIds[i] < this._bestOfScreenRight) {
                    this._bestOfScreenRight = this._paramsScreen.rolesIds[i];
                }
            }
        }
    }
}

export interface TestItemRightsList {
    view: boolean;
    edit: boolean;
    duplicate: boolean;
    delete: boolean;
}

const defaultRights: TestItemRightsList = {
  view: false,
  edit: false,
  duplicate: false,
  delete: false,
};

export function mapTestItemRightsToRowRights(testRights: string []): TestItemRightsList {
  const rights = { ...defaultRights };

  testRights.forEach((right: string) => {
    if (right in rights) {
      rights[right as keyof TestItemRightsList] = true;
    }
  });

  return rights;
}

export interface TestScreenRightList {
    list: boolean;
    add: boolean;
    edit: boolean;
    delete: boolean;
}

export interface TestItemRightsParams {
    user: User;
    organizationId: number;
    // test: Test;
    rolesIds: number[];
    athleteCoachRelations: AthleteCoach[];
    athleteMemberships: OrganizationMembership[];
    coaches: Coach[];
    athlete: Athlete;
}

export interface TestScreenRightsParams {
    rolesIds: number[];
}
